import { useOrderStore } from 'modules/orders/store'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useDebounce } from 'utils'
import { recalculateOrderTotals } from './recalculateOrderTotals'
import { useRecalculeItems } from './useRecalculeItems'

/**
 * Recálculo automático na duplicação: ao abrir (cliente atual) e ao trocar o cliente.
 * Deve ficar dentro do FormProvider (montado em Form.tsx).
 */
export const DuplicateOrderRecalcHandler = () => {
  const pageLocation = useLocation()
  const isDuplicatePage = pageLocation.pathname.includes('/duplicate')

  const { watch, setValue, getValues } = useFormContext()
  const setForm = useOrderStore.useSetForm()
  const addProductsForm = useOrderStore.useAddProductsForm()
  const setIsTaxesRecalculePending =
    useOrderStore.useSetIsTaxesRecalculePending()
  const setIsRecalculatingDuplicateOrder =
    useOrderStore.useSetIsRecalculatingDuplicateOrder()
  const orderForm = useOrderStore.useForm()

  const customer = watch('customer')
  const [paymentTerm, orderType, unity] = watch([
    'paymentTerm',
    'orderType',
    'unity'
  ])
  const totalDiscount = watch('totalDiscount')
  const totalExpenses = watch('totalExpenses')
  const freightPrice = watch('freightPrice')
  const discountDeb = useDebounce(totalDiscount, 500)
  const expensesDeb = useDebounce(totalExpenses, 500)
  const freightPriceDeb = useDebounce(freightPrice, 500)

  const previousCustomerIdRef = useRef<string | undefined>()
  const completedRecalcSessionsRef = useRef<Set<string>>(new Set())
  const isRecalcInProgressRef = useRef(false)
  const [shouldRecalcItems, setShouldRecalcItems] = useState(false)
  const [itemsToRecalc, setItemsToRecalc] = useState<any[]>([])
  const [recalcKey, setRecalcKey] = useState(0)
  const recalcContextRef = useRef<{
    paymentTermId?: string
    orderTypeId?: string
    unityId?: string
    customerId?: string
  }>({})

  const recalculateTotals = useCallback(
    async (items: any[]) => {
      try {
        const total = await recalculateOrderTotals({
          orderTypeId: orderType?.id || recalcContextRef.current.orderTypeId,
          customerId: customer?.id || recalcContextRef.current.customerId,
          unityId: unity?.id || recalcContextRef.current.unityId,
          paymentTermId:
            paymentTerm?.id ||
            getValues('paymentTerm')?.id ||
            recalcContextRef.current.paymentTermId,
          items,
          totalDiscount: Number(discountDeb || 0),
          totalExpenses: Number(expensesDeb || 0),
          freightPrice: Number(freightPriceDeb || 0)
        })

        if (total) {
          setForm({ total })
          setIsTaxesRecalculePending(false)
        }
      } catch {
        toast.error('Não foi possível recalcular os totais do pedido!', {
          toastId: 'recalcule-duplicate-totals'
        })
      } finally {
        setIsRecalculatingDuplicateOrder(false)
        isRecalcInProgressRef.current = false
      }
    },
    [
      customer?.id,
      discountDeb,
      expensesDeb,
      freightPriceDeb,
      getValues,
      orderType?.id,
      paymentTerm?.id,
      setForm,
      setIsRecalculatingDuplicateOrder,
      setIsTaxesRecalculePending,
      unity?.id
    ]
  )

  const handleRecalcComplete = useCallback(
    async (items: any[]) => {
      setShouldRecalcItems(false)
      setItemsToRecalc([])
      await recalculateTotals(items)
    },
    [recalculateTotals]
  )

  const handleRecalcError = useCallback(() => {
    setItemsToRecalc((items) => {
      if (items.length) {
        addProductsForm(items)
      }
      return []
    })
    setShouldRecalcItems(false)
    setIsRecalculatingDuplicateOrder(false)
    isRecalcInProgressRef.current = false
  }, [addProductsForm, setIsRecalculatingDuplicateOrder])

  useRecalculeItems({
    enabled: isDuplicatePage && shouldRecalcItems && itemsToRecalc.length > 0,
    productsOverride: itemsToRecalc,
    recalcKey,
    onRecalcComplete: handleRecalcComplete,
    onRecalcError: handleRecalcError
  })

  const startDuplicateRecalc = useCallback(
    (items: any[], sessionKey: string) => {
      if (!items?.length || isRecalcInProgressRef.current) return
      if (completedRecalcSessionsRef.current.has(sessionKey)) return

      completedRecalcSessionsRef.current.add(sessionKey)
      isRecalcInProgressRef.current = true

      setIsTaxesRecalculePending(true)
      setIsRecalculatingDuplicateOrder(true)
      const snapshot = orderForm?.duplicateRecalcContext
      recalcContextRef.current = {
        paymentTermId: getValues('paymentTerm')?.id || snapshot?.paymentTermId,
        orderTypeId: getValues('orderType')?.id || snapshot?.orderTypeId,
        unityId: getValues('unity')?.id || snapshot?.unityId,
        customerId: getValues('customer')?.id || snapshot?.customerId
      }
      setItemsToRecalc([...items])
      setForm({ orderItems: [] })
      setRecalcKey((key) => key + 1)
      setShouldRecalcItems(true)
    },
    [
      getValues,
      orderForm?.duplicateRecalcContext,
      setForm,
      setIsRecalculatingDuplicateOrder,
      setIsTaxesRecalculePending
    ]
  )

  useEffect(() => {
    if (!isDuplicatePage || !customer?.id || !orderForm?.orderId) return
    if (isRecalcInProgressRef.current || shouldRecalcItems) return

    const customerId = customer.id
    const orderId = orderForm.orderId
    const orderItems = orderForm?.orderItems

    if (!orderItems?.length) return

    const sessionKey = `${orderId}:${customerId}`

    if (completedRecalcSessionsRef.current.has(sessionKey)) return

    const isCustomerChange =
      previousCustomerIdRef.current !== undefined &&
      previousCustomerIdRef.current !== customerId

    previousCustomerIdRef.current = customerId

    if (isCustomerChange) {
      completedRecalcSessionsRef.current.delete(sessionKey)
      setValue('cdPedidoReserva', null)
      setValue('paymentResponsible', customer)

      const currentInstallments = getValues('installments') || []
      const duplicateInstallmentsSnapshot = currentInstallments.map(
        (installment: any) => ({
          days: installment?.days,
          dueDate: installment?.dueDate,
          value: installment?.value,
          paymentMethod: installment?.paymentMethod ?? null
        })
      )

      setForm({
        paymentResponsible: customer,
        total: null,
        duplicateInstallmentsSnapshot
      })
    }

    startDuplicateRecalc(orderItems, sessionKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uma vez por orderId+cliente
  }, [
    customer?.id,
    isDuplicatePage,
    orderForm?.orderId,
    orderForm?.orderItems?.length
  ])

  return null
}
